import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import BaseTable, { Column } from 'react-base-table';
import { useFrustrationSignalConfigs } from '@src/hooks/frustrationSignal/useFrustrationSignalConfigs';
import { TableWrapper } from './styled';
import { SortableRow } from './SortableRow';
import { TableHeader } from './TableHeader';
import { FRUSTRATION_SIGNAL_METRICS_DISPLAY } from '@src/utils/flow/meta';
import useFrustrationSignalOverview from '../../hooks/useFrustrationSignalOverview';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import useDndUtils from '../hooks/useDndUtils';

const dragHandleWidth = 32;

// Extract row renderer as a separate component to avoid hooks-in-callback linting error
const RowRenderer = ({ rowData, nameColumnWidth, columnWidth, onDataUpdate }) => (
  <SortableRow
    signalMeta={rowData}
    nameColumnWidth={nameColumnWidth}
    columnWidth={columnWidth}
    onDataUpdate={onDataUpdate}
  />
);

export default function FrustrationTable() {
  const { frustrationSignals } = useFrustrationSignalOverview();
  const frustrationSignalConfigs = useFrustrationSignalConfigs();
  const [width, setWidth] = useState(800);
  const [signalDataMap, setSignalDataMap] = useState<Record<string, number>>({});
  const containerRef = useRef(null);
  const { handleDragEnd } = useDndUtils(frustrationSignals);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 100, tolerance: 5 },
    }),
  );

  const rows = useMemo(() => {
    return frustrationSignals.map((signalId) => {
      const config = frustrationSignalConfigs[signalId];
      return config
        ? { ...config, sortValue: signalDataMap[signalId] || 0 }
        : { id: signalId, name: 'Unknown Signal', isDeleted: true, sortValue: 0 };
    });
  }, [frustrationSignals, frustrationSignalConfigs, signalDataMap]);

  // Calculate equal column widths; effectiveNameWidth narrows the name cell for the drag handle
  const columnCount = 4; // name, count, impactedSessions, impactedUsers
  const columnWidth = useMemo(() => width / columnCount, [width, columnCount]);
  const effectiveNameWidth = useMemo(() => columnWidth - dragHandleWidth, [columnWidth]);

  const rowHeight = 72;
  const headerHeight = 38;
  const height = rows.length * rowHeight + headerHeight;

  // Update width based on container size
  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      if (entries.length > 0) {
        const containerWidth = entries[0].contentRect.width;
        setWidth(containerWidth);
      }
    });
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => observer.disconnect();
  }, []);

  const rowRenderer = useCallback(
    ({ rowData }) => (
      <RowRenderer
        rowData={rowData}
        nameColumnWidth={effectiveNameWidth}
        columnWidth={columnWidth}
        onDataUpdate={setSignalDataMap}
      />
    ),
    [effectiveNameWidth, columnWidth, setSignalDataMap],
  );

  return (
    <TableWrapper ref={containerRef} className="frustration-table-container">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={frustrationSignals} strategy={verticalListSortingStrategy}>
          <BaseTable
            data={rows}
            rowKey="id"
            width={width}
            height={height}
            rowHeight={rowHeight}
            headerHeight={headerHeight}
            rowClassName={() => 'sortable-row'}
            rowRenderer={rowRenderer}
            overscanColumnCount={1}
          >
            <Column
              key="name"
              dataKey="name"
              width={columnWidth}
              align="left"
              frozen="left"
              headerRenderer={() => (
                <TableHeader title="Signal Name" paddingLeft={`${dragHandleWidth}px`} />
              )}
            />
            <Column
              key="count"
              dataKey="count"
              width={columnWidth}
              align="left"
              headerRenderer={() => (
                <TableHeader title={FRUSTRATION_SIGNAL_METRICS_DISPLAY.count.shortName} />
              )}
            />
            <Column
              key="impactedSessions"
              dataKey="impactedSessions"
              width={columnWidth}
              align="left"
              headerRenderer={() => (
                <TableHeader title={FRUSTRATION_SIGNAL_METRICS_DISPLAY.with_session.shortName} />
              )}
            />
            <Column
              key="impactedUsers"
              dataKey="impactedUsers"
              width={columnWidth}
              align="left"
              headerRenderer={() => (
                <TableHeader title={FRUSTRATION_SIGNAL_METRICS_DISPLAY.with_user.shortName} />
              )}
            />
          </BaseTable>
        </SortableContext>
      </DndContext>
    </TableWrapper>
  );
}
